import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchEmailAccounts,
  fetchEmailAccount,
  createEmailAccount,
  updateEmailAccount,
  deleteEmailAccount,
  EmailAccount
} from '@/services/api/emailAccounts';
import { toast } from '@/hooks/use-toast';

const QUERY_KEY = 'emailAccounts';

export const useEmailAccounts = () => {
  const queryClient = useQueryClient();

  // Fetch all accounts
  const {
    data: accounts = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: fetchEmailAccounts,
  });

  // Create account mutation
  const createMutation = useMutation({
    mutationFn: createEmailAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Success',
        description: 'Email account created successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create email account',
        variant: 'destructive',
      });
    },
  });

  // Update account mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<EmailAccount> }) =>
      updateEmailAccount(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Success',
        description: 'Email account updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update email account',
        variant: 'destructive',
      });
    },
  });

  // Delete account mutation
  const deleteMutation = useMutation({
    mutationFn: deleteEmailAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Success',
        description: 'Email account deleted successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete email account',
        variant: 'destructive',
      });
    },
  });

  return {
    accounts,
    isLoading,
    error,
    refetch,
    createAccount: createMutation.mutateAsync,
    updateAccount: (id: number, data: Partial<EmailAccount>) =>
      updateMutation.mutateAsync({ id, data }),
    deleteAccount: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

// Hook to fetch a single account
export const useEmailAccount = (accountId: number | null) => {
  return useQuery({
    queryKey: [QUERY_KEY, accountId],
    queryFn: () => fetchEmailAccount(accountId!),
    enabled: accountId !== null,
  });
};
